import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import DailyAgenda from '../components/appointments/DailyAgenda';
import AppointmentList from '../components/appointments/AppointmentList';
import AppointmentCalendar from '../components/appointments/AppointmentCalendar';
import AppointmentForm from '../components/appointments/AppointmentForm';
import appointmentService from '../services/appointmentService';

const Appointments = () => {
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [view, setView] = useState('calendar');
  const [stats, setStats] = useState(null);
  const [preSelectedDate, setPreSelectedDate] = useState(null);
  const [preSelectedPatient, setPreSelectedPatient] = useState(null);

  // Check for pre-selected patient from navigation
  useEffect(() => {
    if (location.state?.preSelectedPatient) {
      setPreSelectedPatient(location.state.preSelectedPatient);
      setShowForm(true);
      // Clear the navigation state to prevent form from reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    fetchStats();
  }, [refreshKey]);

  const fetchStats = async () => {
    try {
      const response = await appointmentService.getAppointmentStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAddAppointment = (date = null) => {
    setSelectedAppointment(null);
    setPreSelectedPatient(null);
    if (date) {
      setPreSelectedDate(date.toISOString().split('T')[0]);
    } else {
      setPreSelectedDate(null);
    }
    setShowForm(true);
  };

  const handleDateSelect = (date) => {
    setPreSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setPreSelectedPatient(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedAppointment(null);
    setPreSelectedDate(null);
    setPreSelectedPatient(null);
  };

  const handleSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <div className="flex-1 p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Appointments</h1>
            <p className="text-gray-600 mt-1">
              Manage your appointment schedule
            </p>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">Today</p>
                <p className="text-2xl font-bold text-blue-700">{stats.today}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-600 font-medium">Scheduled</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.scheduled}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Completed</p>
                <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600 font-medium">Missed</p>
                <p className="text-2xl font-bold text-red-700">{stats.missed}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600 font-medium">Total</p>
                <p className="text-2xl font-bold text-purple-700">{stats.total}</p>
              </div>
            </div>
          )}

          {/* View Toggle */}
          <div className="mb-6 flex space-x-2">
            <button
              onClick={() => setView('calendar')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                view === 'calendar'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
               Calendar
            </button>
            <button
              onClick={() => setView('agenda')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                view === 'agenda'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
               Daily Agenda
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                view === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
               All Appointments
            </button>
          </div>

          {/* Content */}
          {view === 'calendar' ? (
            <AppointmentCalendar
              refreshKey={refreshKey}
              onDateSelect={handleDateSelect}
              onAddAppointment={handleAddAppointment}
            />
          ) : view === 'agenda' ? (
            <DailyAgenda
              refreshKey={refreshKey}
              onEditAppointment={handleEditAppointment}
            />
          ) : (
            <AppointmentList
              refreshKey={refreshKey}
              onEditAppointment={handleEditAppointment}
              onAddAppointment={handleAddAppointment}
            />
          )}

          {/* Floating Add Button (for agenda and calendar view) */}
          {(view === 'agenda' || view === 'calendar') && (
            <button
              onClick={() => handleAddAppointment()}
              className="fixed bottom-8 right-8 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition-colors"
              title="Schedule Appointment"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Appointment Form Modal */}
      {showForm && (
        <AppointmentForm
          appointment={selectedAppointment}
          preSelectedDate={preSelectedDate}
          preSelectedPatient={preSelectedPatient}
          onClose={handleCloseForm}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default Appointments;